/**
 * PHARMAKON — barcode wedge-scanner convention (design/ux/01_foundations.md
 * §4.8, refonte WP-17; OP-ACH-25 « beep off by default »).
 *
 * A wedge scanner types very fast and ends with Entrée (some are configured
 * to end with Tab). A burst of at least SCAN_MIN_LENGTH characters whose
 * inter-key gaps are all ≤ the workstation's maximum gap, ended by a
 * terminator, is a scan; anything slower is human typing and follows the
 * normal field rules. Read by hooks/useScanInput.js and lib/scan-detector.js
 * — never as literals (rules/development.md §4).
 *
 * Owner classes (rules/development.md §4.2): the thresholds are build-time;
 * the gap and the beep are per-WORKSTATION preferences (a slower scanner
 * raises the gap in « Préférences d'affichage »), kept in localStorage like
 * the other display preferences (OP-F-06) — never on the server.
 */

/** Shortest code accepted as a scan (EAN-8 = 8, internal lot labels ≥ 6). */
export const SCAN_MIN_LENGTH = 6;

/** Default maximum gap between two scanned characters (ms). */
export const SCAN_MAX_INTER_KEY_MS = 35;

/** Keys that end a burst. */
export const SCAN_TERMINATORS = Object.freeze(["Enter", "Tab"]);

/** Audible feedback after a scan — off by default (OP-ACH-25). */
export const SCAN_BEEP_DEFAULT = false;

/**
 * Gaps a workstation may choose in the display preferences (ms). The first
 * is the default; slower scanners (Bluetooth, some USB-HID models) need more.
 */
export const SCAN_GAP_OPTIONS = Object.freeze([
  Object.freeze({ value: SCAN_MAX_INTER_KEY_MS, label: "Standard (35 ms)" }),
  Object.freeze({ value: 60, label: "Lecteur lent (60 ms)" }),
  Object.freeze({ value: 100, label: "Lecteur très lent (100 ms)" }),
]);

/** Beep tone (Web Audio): short, mid-pitch, quiet. */
export const SCAN_BEEP = Object.freeze({ FREQUENCY_HZ: 1200, DURATION_MS: 80, GAIN: 0.08 });

/** localStorage key of the per-workstation scanner preferences. */
export const SCANNER_PREFS_STORAGE_KEY = "pharmakon_scanner_prefs";

/** Shown when a scan lands on a screen that has no use for it (§4.8). */
export const SCAN_UNUSED_MESSAGE = "Code scanné non utilisé sur cet écran";
